<template>
  <div class="data-source-settings">
    <el-table :data="sources" border>
      <el-table-column label="数据源" min-width="210">
        <template slot-scope="{ row }">
          <div class="source-name">{{ row.name }}</div>
          <div class="source-description">{{ row.description }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="scope" label="使用范围" min-width="260"/>
      <el-table-column label="连接状态" width="120">
        <template slot-scope="{ row }">
          <el-tag v-if="row.error" type="danger" size="small">状态异常</el-tag>
          <el-tag v-else-if="row.configured" type="success" size="small">已连接</el-tag>
          <el-tag v-else type="warning" size="small">未连接</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="凭证配置" min-width="180">
        <template slot-scope="{ row }">
          <span v-if="row.error" class="status-error">{{ row.error }}</span>
          <template v-else>
            <div>{{ row.credential }}</div>
            <div v-if="row.sourceLabel" class="credential-source">{{ row.sourceLabel }}</div>
          </template>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="190" align="right">
        <template slot-scope="{ row }">
          <template v-if="row.key === 'collector'">
            <el-button type="primary" plain size="small" :disabled="deleting" @click="startCollectorEdit">
              {{ configured ? '修改凭证' : '配置凭证' }}
            </el-button>
            <el-button
              v-if="configured"
              type="danger"
              plain
              size="small"
              :loading="deleting"
              :disabled="saving"
              @click="removeKey"
            >{{ deleting ? '删除中' : '删除' }}</el-button>
          </template>
          <template v-else>
            <el-button
              type="primary"
              plain
              size="small"
              :disabled="operatorDeleting"
              @click="startOperatorEdit"
            >{{ operatorDatabaseConfigured ? '修改凭证' : '配置凭证' }}</el-button>
            <el-button
              v-if="operatorDatabaseConfigured"
              type="danger"
              plain
              size="small"
              :loading="operatorDeleting"
              :disabled="operatorSaving"
              @click="removeOperatorKey"
            >{{ operatorDeleting ? '删除中' : '删除' }}</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <el-alert
      class="source-note"
      type="info"
      :closable="false"
      show-icon
      title="公开数据采集与 Codex 运营代理使用两套独立凭证，修改其中一项不会影响另一项。"
    />

    <div v-if="(!configured && !operatorEditing) || editing" class="credential-form">
      <div class="form-title">配置 TikHub 公开数据采集凭证</div>
      <div class="form-description">该凭证同时用于公开舆情、账号与作品运营监测、评论采集。</div>
      <el-input
        v-model.trim="key"
        type="password"
        show-password
        :disabled="saving || deleting"
        :placeholder="configured ? '输入新的 TikHub API Key' : '粘贴 TikHub API Key'"
      />
      <div class="form-actions">
        <el-button type="primary" :loading="saving" :disabled="deleting" @click="save">
          {{ saving ? '保存中' : configured ? '保存修改' : '保存并连接' }}
        </el-button>
        <el-button v-if="configured" :disabled="saving || deleting" @click="cancelEdit">取消</el-button>
      </div>
    </div>

    <div v-if="operatorEditing" class="credential-form">
      <div class="form-title">配置 TikHub Codex 运营代理凭证</div>
      <div class="form-description">
        保存后立即用于运营代理接口，不影响舆情监测和作品运营数据采集。
      </div>
      <el-input
        v-model.trim="operatorKey"
        type="password"
        show-password
        :disabled="operatorSaving || operatorDeleting"
        :placeholder="operatorDatabaseConfigured ? '输入新的运营代理 TikHub API Key' : '粘贴运营代理 TikHub API Key'"
      />
      <div class="form-actions">
        <el-button
          type="primary"
          :loading="operatorSaving"
          :disabled="operatorDeleting"
          @click="saveOperatorKey"
        >{{ operatorSaving ? '保存中' : operatorDatabaseConfigured ? '保存修改' : '保存并连接' }}</el-button>
        <el-button
          :disabled="operatorSaving || operatorDeleting"
          @click="cancelOperatorEdit"
        >取消</el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    operatorValue: {
      type: Object,
      default: () => ({})
    },
    operatorError: {
      type: String,
      default: ''
    },
    saving: Boolean,
    deleting: Boolean,
    operatorSaving: Boolean,
    operatorDeleting: Boolean
  },
  data () {
    return {
      key: '',
      editing: false,
      operatorKey: '',
      operatorEditing: false
    }
  },
  computed: {
    configured () {
      return Boolean(this.value && this.value.configured)
    },
    operatorDatabaseConfigured () {
      return Boolean(this.operatorValue && this.operatorValue.source === 'DATABASE')
    },
    sources () {
      return [
        {
          key: 'collector',
          name: 'TikHub 公开数据采集',
          description: '系统采集任务使用',
          scope: '舆情监测、账号与作品运营监测、评论采集',
          configured: this.configured,
          credential: this.value.masked_key || '尚未配置',
          sourceLabel: this.configured ? '页面配置' : '',
          error: ''
        },
        {
          key: 'operator',
          name: 'TikHub Codex 运营代理',
          description: '运营通过 Codex 查询公开数据',
          scope: '运营账号授权范围内的 TikHub 代理接口',
          configured: Boolean(this.operatorValue && this.operatorValue.configured),
          credential: this.operatorCredential,
          sourceLabel: this.operatorSourceLabel,
          error: this.operatorError
        }
      ]
    },
    operatorCredential () {
      if (this.operatorDatabaseConfigured) {
        return this.operatorValue.masked_key || '已配置'
      }
      if (this.operatorValue && this.operatorValue.source === 'SERVER_CONFIG') {
        return '服务器固定凭证'
      }
      return '尚未配置'
    },
    operatorSourceLabel () {
      if (this.operatorDatabaseConfigured) return '页面配置，保存后立即生效'
      if (this.operatorValue && this.operatorValue.source === 'SERVER_CONFIG') {
        return '服务器配置，可通过页面配置覆盖'
      }
      return ''
    }
  },
  watch: {
    value: {
      deep: true,
      handler () {
        this.key = ''
        this.editing = false
      }
    },
    operatorValue: {
      deep: true,
      handler () {
        this.operatorKey = ''
        this.operatorEditing = false
      }
    }
  },
  methods: {
    save () {
      if (this.saving || this.deleting) return
      if (!this.key) return this.$message.warning('请输入 TikHub API Key')
      this.$emit('save-key', this.key)
    },
    startCollectorEdit () {
      this.operatorEditing = false
      this.operatorKey = ''
      this.editing = true
    },
    cancelEdit () {
      this.key = ''
      this.editing = false
    },
    saveOperatorKey () {
      if (this.operatorSaving || this.operatorDeleting) return
      if (!this.operatorKey) return this.$message.warning('请输入运营代理 TikHub API Key')
      this.$emit('save-operator-key', this.operatorKey)
    },
    startOperatorEdit () {
      this.editing = false
      this.key = ''
      this.operatorEditing = true
    },
    cancelOperatorEdit () {
      this.operatorKey = ''
      this.operatorEditing = false
    },
    removeKey () {
      if (this.saving || this.deleting) return
      this.$confirm(
        '删除后，舆情监测、账号与作品运营监测、评论采集都会停止；Codex 运营代理不受影响。确定删除吗？',
        '删除公开数据采集凭证',
        {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.$emit('delete-key')
      }).catch(() => undefined)
    },
    removeOperatorKey () {
      if (this.operatorSaving || this.operatorDeleting) return
      this.$confirm(
        '删除页面配置后，将优先回退到服务器凭证；如果服务器未配置，Codex 运营代理将停止调用。确定删除吗？',
        '删除运营代理凭证',
        {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.$emit('delete-operator-key')
      }).catch(() => undefined)
    }
  }
}
</script>

<style scoped>
.source-name {
  color: #303133;
  font-weight: 600;
  line-height: 22px;
}

.source-description,
.form-description {
  margin-top: 3px;
  color: #909399;
  font-size: 12px;
  line-height: 20px;
}

.status-error {
  color: #f56c6c;
  font-size: 12px;
}

.credential-source {
  margin-top: 3px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.source-note {
  margin-top: 16px;
}

.credential-form {
  max-width: 680px;
  margin-top: 16px;
  padding: 18px 20px;
  border: 1px solid #ebeef5;
  background: #fafafa;
}

.form-title {
  color: #303133;
  font-weight: 600;
}

.form-description {
  margin-bottom: 12px;
}

.form-actions {
  margin-top: 14px;
}
</style>
